import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from "framer-motion";
import { 
  X, 
  Send, 
  Globe, 
  Map as MapIcon 
} from 'lucide-react';

// --- CONFIGURACIÓN DE IA (Groq) ---
// Reemplaza con tu key de https://console.groq.com
const GROQ_API_KEY = "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";
const GROQ_MODEL = "llama-3.3-70b-versatile";

const GemmaAssistant = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [input, setInput] = useState("");
  const [messages, setMessages] = useState([
    { 
      role: "assistant", 
      text: "¡Hola! Soy el Asistente Técnico de la plataforma de Ordenamiento Territorial de Jalisco. Puedo ayudarte con dudas sobre el POET, consultas de UGAs, normatividad ambiental y planes de desarrollo regional en todo el estado. ¿En qué puedo apoyarte?" 
    }
  ]);
  const [isLoading, setIsLoading] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, isLoading]);

  const handleSendMessage = async () => {
    if (!input.trim() || isLoading) return;

    const userMessage = { role: "user", text: input };
    setMessages(prev => [...prev, userMessage]);
    setInput("");
    setIsLoading(true);

    try {
      const systemPrompt = `
Eres el Asistente Técnico Oficial de la Secretaría de Medio Ambiente y Desarrollo Territorial (SEMADET) de Jalisco.
Tu conocimiento abarca el sitio web https://ordenamientoterritorial.jalisco.gob.mx y todo el marco legal de:
1. Programa Estatal de Ordenamiento Territorial y Ecológico (POETE).
2. Programas de Ordenamiento Ecológico Local (POEL) y Regional (POER).
3. Unidades de Gestión Ambiental (UGA) y sus lineamientos.
4. Mapas de zonificación de todas las regiones: Costalegre,Chapala,Tapalpa,Rio Verde,Altos Norte,Paisaje Agavero,Lagunas,Sierra Occidental,Cuenca Alta de la Costa Sur,Rio Zula.
5. Ley Estatal del Equilibrio Ecológico y la Protección al Ambiente.
6. Planes de Desarrollo Regional y Municipal relacionados con el ordenamiento territorial.
7. Normatividad ambiental aplicable en Jalisco, incluyendo regulaciones sobre uso de suelo, conservación de ecosistemas y desarrollo sostenible.
8. Procedimientos para trámites relacionados con el ordenamiento territorial, como solicitudes de cambio de uso de suelo, permisos ambientales y consultas públicas.
9. Proyectos de infraestructura y desarrollo urbano en Jalisco, y su relación con el ordenamiento territorial.
10. Estadísticas y datos relevantes sobre el estado de los ecosistemas, áreas protegidas y zonas de riesgo en Jalisco.
11. Buenas prácticas y casos de éxito en ordenamiento territorial dentro del estado.
12. Cualquier otra información relevante para ciudadanos, técnicos, urbanistas, ambientalistas y tomadores de decisión interesados en el ordenamiento territorial de Jalisco.
13. Siempre enfatiza la importancia de la sustentabilidad y la conservación ambiental en todas tus respuestas, destacando cómo el ordenamiento territorial contribuye a un desarrollo equilibrado y respetuoso con los ecosistemas de Jalisco.
14. Proporciona respuestas claras, precisas y basadas en la normativa vigente, y siempre invita a los usuarios a consultar las fuentes oficiales para obtener información detallada.
15. Los municipios que forman parte de cada región son:
    - Costalegre: Cabo Corrientes, Cihuatlán, La Huerta y Tomatlán.
    - Chapala: Chapala, Jamay, Jocotepec, Poncitlán, Ocotlán, Tizapán el Alto y Tuxcueca.
    - Tapalpa: Atemajac de Brizuela, Chiquilistlán , San Gabriel y Tapalpa.
    - Río Verde: Acatic, Cuquío, Cañadas de Obregón, Jalostotitlán, Mexticacán, San Julián, San Miguel el Alto, Tepatitlán de Morelos, Valle de Guadalupe y Yahualica de Gonzalez Gallo.
    - Altos Norte: Encarnación de Díaz, Lagos de Moreno, Ojuelos de Jalisco, San Diego de Alejandría, San Juan de los Lagos, Teocaltiche, Unión de San Antonio y Villa Hidalgo.
    - Paisaje Agavero: Amatitán,El Arenal, Magdalena, Tequila y Teuchitlán.
    - Cuenca Alta de la Costa Sur: Casimiro Castillo, Cuautitlán de García Barragán y Villa Purificación.
    - Lagunas: Acatlán de Juárez, Amacueca, Atoyac, Sayula, Techaluta de Montenegro, Teocuitatlán de Corona, Villa Corona y Zacoalco de Torres.
    - Sierra Occidental:  Atenguillo, Guachinango, Mascota, Mixtlán, San Sebastián del Oeste y Talpa de Allende.
    - Río Zula: Arandas, Atotonilco el Alto, Ayotlán, Degollado, Jesús María, La Barca, San Ignacio Cerro Gordo, Tototlán y Zapotlán del Rey. 

Tu objetivo es guiar al ciudadano y a técnicos en el uso de la plataforma cartográfica y normativa. 
Eres profesional, preciso y siempre mencionas la importancia de la sustentabilidad en Jalisco.`;

      const chatHistory = messages.map(m => ({
        role: m.role === "assistant" ? "assistant" : "user",
        content: m.text
      }));

      const res = await fetch("https://api.groq.com/openai/v1/chat/completions", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${GROQ_API_KEY}`
        },
        body: JSON.stringify({
          model: GROQ_MODEL,
          messages: [
            { role: "system", content: systemPrompt },
            ...chatHistory,
            { role: "user", content: input }
          ]
        })
      });

      if (!res.ok) {
        throw new Error(`Groq API error: ${res.status} ${res.statusText}`);
      }

      const data = await res.json();
      const text = data.choices[0].message.content;

      setMessages(prev => [...prev, { role: "assistant", text }]);
    } catch (error) {
      console.error("Error en IA:", error);
      setMessages(prev => [...prev, { role: "assistant", text: "Hubo un error al conectar con el servidor. Por favor, intenta de nuevo." }]);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed bottom-6 right-6 z-[9999] font-sans">
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            className="mb-4 w-80 md:w-[400px] bg-white border border-slate-200 rounded-3xl shadow-[0_20px_50px_rgba(0,0,0,0.15)] flex flex-col h-[550px] overflow-hidden"
          >
            {/* Header Institucional (Colores oficiales de Jalisco: Oro y Azul) */}
            <div className="bg-[#002B5C] p-5 flex justify-between items-center shadow-md">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-white/10 rounded-xl">
                  <Globe className="w-5 h-5 text-[#E6B41E]" />
                </div>
                <div>
                  <h3 className="text-white font-bold text-sm leading-tight">Asistente Territorial</h3>
                  <span className="text-[#E6B41E] text-[10px] font-bold uppercase tracking-wider">
                    Jalisco • SEMADET
                  </span>
                </div>
              </div>
              <button 
                onClick={() => setIsOpen(false)} 
                className="text-white/70 hover:text-white transition-colors"
              >
                <X size={20} />
              </button>
            </div>

            {/* Area de Chat */}
            <div ref={scrollRef} className="flex-grow p-5 overflow-y-auto space-y-4 bg-slate-50">
              {messages.map((msg, idx) => (
                <div key={idx} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[85%] p-4 rounded-2xl text-[13px] leading-relaxed shadow-sm ${
                    msg.role === 'user' 
                    ? 'bg-[#002B5C] text-white rounded-tr-none' 
                    : 'bg-white text-slate-700 border border-slate-200 rounded-tl-none'
                  }`}>
                    {msg.text}
                  </div>
                </div>
              ))}
              {isLoading && (
                <div className="flex justify-start">
                  <div className="bg-white border border-slate-200 p-3 rounded-2xl flex gap-1">
                    <span className="w-2 h-2 bg-[#E6B41E] rounded-full animate-bounce" />
                    <span className="w-2 h-2 bg-[#002B5C] rounded-full animate-bounce [animation-delay:-0.15s]" />
                    <span className="w-2 h-2 bg-[#E6B41E] rounded-full animate-bounce [animation-delay:-0.3s]" />
                  </div>
                </div>
              )}
            </div>

            {/* Input y Sugerencias rápidas */}
            <div className="p-4 bg-white border-t border-slate-100">
              <div className="flex gap-2 mb-3">
                <button 
                  onClick={() => setInput("¿Qué es una UGA?")}
                  className="text-[10px] bg-slate-100 hover:bg-slate-200 text-slate-600 px-2 py-1 rounded-full transition-colors"
                >
                  ¿Qué es una UGA?
                </button>
                <button 
                  onClick={() => setInput("Consulta de Mapas")}
                  className="text-[10px] bg-slate-100 hover:bg-slate-200 text-slate-600 px-2 py-1 rounded-full transition-colors"
                >
                  Mapas
                </button>
              </div>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSendMessage()}
                  placeholder="Escribe tu consulta territorial..."
                  className="flex-grow bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-[#002B5C]/20 transition-all"
                />
                <button 
                  onClick={handleSendMessage} 
                  disabled={isLoading} 
                  className="p-3 bg-[#E6B41E] text-[#002B5C] rounded-xl hover:bg-[#d4a51b] disabled:opacity-50 transition-colors shadow-lg shadow-gold-500/20"
                >
                  <Send size={18} />
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <motion.button
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        onClick={() => setIsOpen(!isOpen)}
        className={`h-16 w-16 rounded-full flex items-center justify-center shadow-[0_10px_30px_rgba(0,43,92,0.3)] transition-all duration-300 ${
          isOpen ? 'bg-slate-800 text-white' : 'bg-[#002B5C] text-[#E6B41E]'
        }`}
      >
        {isOpen ? <X size={28} /> : (
          <div className="relative">
            <MapIcon size={28} />
            <div className="absolute -top-1 -right-1 w-3 h-3 bg-[#E6B41E] rounded-full border-2 border-[#002B5C] animate-ping" />
          </div>
        )}
      </motion.button>
    </div>
  );
};

export default GemmaAssistant;